import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Modal,
  Select,
  Spin,
  message,
} from '@surecompanies/core_components'
import React, { useEffect, useState } from 'react'
import {
  fetchPlanSnapshot,
  getAvailablePlanYears,
} from '../api/plan-management-tasks-api'
import styles from '../PlanManagementV2.module.scss'

function FetchPlanModal({
  open,
  onOk,
  onCancel,
}: {
  open: boolean
  onOk: () => void
  onCancel: () => void
}) {
  const queryClient = useQueryClient()
  const [selectedYear, setSelectedYear] = useState<number | undefined>(
    undefined
  )

  const fetchMutation = useMutation({
    mutationFn: (planYear: number) => fetchPlanSnapshot({ planYear }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['planDataSnapshotList'] })
    },
  })

  const isFetchingPlan = fetchMutation.isPending

  const {
    data: availableYears,
    isLoading: isLoadingYears,
    error: yearsError,
  } = useQuery({
    queryKey: ['availablePlanYears'],
    queryFn: getAvailablePlanYears,
    enabled: open,
  })

  useEffect(() => {
    if (open && availableYears && availableYears.length > 0) {
      setSelectedYear(availableYears[0])
    }
  }, [open, availableYears])

  useEffect(() => {
    if (yearsError) {
      console.error('Could not load available plan years', yearsError)
      message.error('An error occurred. Please try again.')
    }
  }, [yearsError])

  const handleConfirm = async () => {
    if (selectedYear === undefined) return
    try {
      await fetchMutation.mutateAsync(selectedYear)
      onOk()
      message.success('Plan fetch request submitted.')
    } catch (e) {
      console.error('Could not start plan fetch', e)
      message.error('An error occurred. Please try again.')
    }
  }

  const handleCancel = () => {
    onCancel()
  }

  const isConfirmDisabled =
    isLoadingYears ||
    !!yearsError ||
    isFetchingPlan ||
    selectedYear === undefined

  return (
    <Modal
      open={open}
      title="Fetch plan data"
      maskClosable={false}
      onCancel={handleCancel}
      footer={[
        <Button
          key="cancel"
          htmlType="button"
          data-testid="fetch-plan-cancel-modal"
          onClick={handleCancel}
        >
          Cancel
        </Button>,
        <Button
          key="confirm"
          htmlType="button"
          type="primary"
          data-testid="fetch-plan-confirm-modal"
          disabled={isConfirmDisabled}
          loading={isFetchingPlan}
          onClick={() => void handleConfirm()}
        >
          Fetch
        </Button>,
      ]}
      centered
    >
      <Spin spinning={isLoadingYears || isFetchingPlan}>
        <div className={styles.modalContent}>
          <Select
            disabled={isLoadingYears || isFetchingPlan}
            value={selectedYear}
            onChange={(v) => setSelectedYear(v)}
            options={(availableYears ?? []).map((y) => ({
              label: String(y),
              value: y,
            }))}
            className={styles.yearSelect}
            aria-label="Plan year"
          />
        </div>
      </Spin>
    </Modal>
  )
}

export default FetchPlanModal
